// src/pages/BookingPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Card, CardContent } from '@mui/material';
import {
  WarningAmber as WarningIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Client, Booking, clientsAPI, bookingsAPI } from '../services/api';
import BookingForm from '../components/forms/BookingForm';
import BookingDetailCard from '../components/cards/BookingDetailCard';
import PageTitle from '../components/common/PageTitle';
import { BeingsPageSubMenu } from '../components/menus';
import { useErrorHandler, ERROR_MESSAGES } from '../errors';
import { PageError } from '../components/common/PageError';
import { getApiErrorMessage } from '../utils/validationHelpers';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../contexts/AuthContext';
import { Permissions } from '../utils/permissions';

const MainContainer = styled(Box)({ position: 'relative' });

const PageContainer = styled(Box)(({ theme }) => ({
  margin: '0 auto',
  maxWidth: '100%',
  padding: '0',
  [theme.breakpoints.down('sm')]: { padding: '0' },
}));

const ContentCard = styled(Card)(({ theme }) => ({
  margin: '0',
  borderRadius: '0',
  border: 'none',
  boxShadow: 'none',
  backgroundColor: theme.palette.background.paper,
  '&:hover': {
    boxShadow: 'none !important',
    borderColor: 'transparent !important',
    transform: 'none !important',
  },
  '&:last-child': { paddingBottom: 0 },
}));

type PageMode = 'new' | 'edit' | 'view';

const BookingPage: React.FC = () => {
  const { id, clientId } = useParams<{ id?: string; clientId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { handleError } = useErrorHandler();
  const { canEditBookings } = usePermissions();
  const { user } = useAuth();
  // Staff cannot read client records; their booking view uses the name and
  // warning flag carried on the booking itself.
  const canViewClients = Permissions.canViewClients(user?.role);

  const [client, setClient] = useState<Client | null>(null);
  const [booking, setBooking] = useState<Booking | undefined>(undefined);
  const [bookingDetail, setBookingDetail] = useState<any>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isEditMode = location.pathname.endsWith('/edit');
  const pageMode: PageMode = !id ? 'new' : isEditMode ? 'edit' : 'view';

  const targetClientId = clientId ? parseInt(clientId) : null;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (pageMode === 'new') {
        if (!targetClientId)
          throw new Error('Client ID is required to create a new booking');
        const loadedClient = await clientsAPI.getById(targetClientId);
        setClient(loadedClient);
        setBooking(undefined);
        setBookingDetail(undefined);
        setLoading(false);
        return;
      }

      if (id) {
        const numericId = parseInt(id);
        if (isNaN(numericId) || numericId <= 0)
          throw new Error('Invalid booking ID');

        const loadedBooking = await bookingsAPI.getById(numericId);
        setBooking(loadedBooking);
        setBookingDetail(loadedBooking);
      }

      setLoading(false);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        ERROR_MESSAGES.OPERATIONS.LOAD_FAILED('booking');
      setError(message);
      setLoading(false);
    }
  }, [pageMode, targetClientId, id]);

  const bookingClientId =
    pageMode !== 'new' && booking?.clientId ? booking.clientId : null;

  // The booking's client is loaded separately so a role change never
  // refetches the booking itself.
  useEffect(() => {
    if (!bookingClientId || !booking) return;
    if (!canViewClients) {
      setClient({
        id: bookingClientId,
        firstName: booking.clientName ?? '',
        lastName: '',
        showWarning: booking.clientShowWarning ?? false,
      } as Client);
      return;
    }
    let cancelled = false;
    clientsAPI
      .getById(bookingClientId)
      .then((loadedClient) => {
        if (!cancelled) setClient(loadedClient);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setError(
          err?.response?.data?.message ||
            err?.message ||
            ERROR_MESSAGES.OPERATIONS.LOAD_FAILED('booking')
        );
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingClientId, canViewClients]);

  const clientPending =
    bookingClientId !== null && client?.id !== bookingClientId && !error;

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFormSuccess = useCallback(
    (savedBooking: Booking) => {
      navigate(`/booking/${savedBooking.id}`);
    },
    [navigate]
  );

  const handleFormCancel = useCallback(() => {
    if (pageMode === 'new' && targetClientId) {
      navigate(`/clients/${targetClientId}/bookings`);
    } else if (pageMode === 'edit') {
      navigate(`/booking/${id}`);
    } else {
      navigate('/bookings');
    }
  }, [pageMode, targetClientId, id, navigate]);

  const handleEditClick = useCallback(() => {
    navigate(`/booking/${id}/edit`);
  }, [id, navigate]);

  if (loading || clientPending) {
    return (
      <PageContainer>
        <LoadingSpinner
          message={
            pageMode === 'new' ? 'Loading client...' : 'Loading booking...'
          }
          size={80}
        />
      </PageContainer>
    );
  }

  if (error || !client) {
    return (
      <PageContainer>
        <PageError message={error || 'Client not found'} onRetry={loadData} />
      </PageContainer>
    );
  }

  const inClientContext = Boolean(targetClientId || client);

  const pageTitle =
    pageMode === 'new'
      ? 'New Booking'
      : pageMode === 'edit'
        ? `Edit Booking ${id}`
        : `Booking #${booking?.id}`;

  // Warning icon (if set), then the Booking type icon, matching BookingsList.
  const getHeaderLeftContent = () => (
    <>
      {client?.showWarning && (
        <Box
          sx={{
            padding: '0 0.5rem',
            display: 'flex',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <WarningIcon
            sx={{
              fontSize: '2.25rem',
              color: '#fff',
              marginLeft: '0.5rem',
              '@media (min-width: 600px)': {
                marginLeft: '2rem',
              },
            }}
          />
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          paddingLeft: client?.showWarning ? 0 : '1rem',
          paddingRight: '0.5rem',
          '@media (min-width: 600px)': {
            paddingLeft: client?.showWarning ? 0 : '3rem',
          },
        }}
      >
        <EventIcon sx={{ fontSize: '2.25rem', color: '#fff' }} />
      </Box>
    </>
  );

  return (
    <MainContainer>
      {inClientContext && (
        <>
          <PageTitle
            title={`${client.firstName} ${client.lastName}`.trim()}
            modeText={pageTitle}
            leftContent={getHeaderLeftContent()}
          />
          {canViewClients && (
            <BeingsPageSubMenu
              being={client}
              type="client"
              showBackButton={false}
              backButtonMode="browser"
            />
          )}
        </>
      )}

      <PageContainer>
        <ContentCard>
          <CardContent sx={{ p: '0', '&:last-child': { pb: 0 } }}>
            <Box>
              {pageMode === 'view' && bookingDetail ? (
                <BookingDetailCard
                  booking={bookingDetail}
                  onEdit={canEditBookings ? handleEditClick : undefined}
                  onRefresh={loadData}
                />
              ) : (
                <BookingForm
                  key={booking?.id || 'new'}
                  client={client}
                  booking={booking}
                  onSuccess={handleFormSuccess}
                  onCancel={handleFormCancel}
                />
              )}
            </Box>
          </CardContent>
        </ContentCard>
      </PageContainer>
    </MainContainer>
  );
};

export default BookingPage;
